/**
 * Partner R2 controls calls for the customer portal (SIL-2383). Thin wrappers over the Hono RPC client for /api/client/r2/*.
 * The routes are gated by the SIL2383_R2_CONTROLS_ENABLED switch AND the org's enrolment: while either is missing every route but
 * /status answers a uniform 404 and the page says the controls are not available. Copy here states results and records only: it never
 * says an item is certified, verified or compliant.
 */
import { api, throwIfNotOk } from './api';

type Loose = (args?: unknown) => Promise<Response>;

export interface R2Status { enabled: boolean; isAdmin: boolean }
export interface R2MonthCounts { month: string; declared: number; received: number; processedRegistered: number; processedSelfReportedOnly: number; outstanding: number }
export interface R2UntestedItem { receiptId: string; assetId: string; receivedOn: string; daysSince: number; band: 'nearing' | 'over' }
export interface R2Summary {
  months: R2MonthCounts[];
  alerts: { nearing: number; over: number; items: R2UntestedItem[] };
  openBatches: Array<{ id: string; receivedOn: string; declaredQty: number; lines: number; mismatch: boolean }>;
  samples: { selectedWithoutResult: number; failedWithoutCorrectiveAction: number };
  dprCount: number;
}
export interface R2Batch { id: string; receivedOn: string; declaredQty: number; lines: number; label: string | null; closedOutcome: string | null; createdAt: string | null }
export interface R2Candidate { assetId: string; serialMasked: string | null; createdAt: string | null }
export interface R2Unit { receiptId: string; assetId: string; serialMasked: string | null; receivedOn: string; clockStart: string; dispositionKind: string | null; releaseBasis: string | null }
export interface R2DrawSummary { id: string; periodStart: string; periodEnd: string; populationCount: number; samplePct: number; sampleSize: number; createdAt: string | null; resultCount: number; failedCount: number }
export interface R2DrawItem { certificateId: string; ordinal: number; serialMasked: string | null; model: string | null; erasedAt: string | null; outcome: string | null; method: string | null; verifiedOn: string | null; correctiveActionRecorded: boolean }
export interface R2DrawDetail {
  draw: { id: string; periodStart: string; periodEnd: string; populationCount: number; samplePct: number; sampleSize: number; seedHex: string | null; algorithmVersion: number; populationRuleVersion: number; populationCutoffAt: string | null; populationSha256: string; createdAt: string | null };
  items: R2DrawItem[];
  evidenceChecked: { systemChecked: number; resulted: number };
}
export interface R2Dpr { designeeId: string; designatedAt: string | null; opsDesignated: boolean; isYou: boolean }
export interface R2DprView { dprs: R2Dpr[]; releases: Array<{ assetId: string; basis: string; releasedAt: string | null }> }
export interface R2RosterRow { userId: string; competency: 'bench_operation' | 'data_sanitization' | 'dpr'; competent: boolean; trainedOn: string | null; expiresOn: string | null; selfRecorded: boolean | null }
export interface R2TeamMember { id: string; email: string; role: 'admin' | 'member'; isActive: boolean }

/** The closed error code of an API refusal, or undefined. */
export function r2ErrorCode(err: unknown): string | undefined {
  const code = (err as { body?: { error?: { code?: unknown } } } | null)?.body?.error?.code;
  return typeof code === 'string' ? code : undefined;
}

async function readJson<T>(p: Promise<Response>): Promise<T> {
  const res = await p;
  await throwIfNotOk(res);
  return (await res.json()) as T;
}

const r2 = () => api.api.client.r2;

export const getR2Status = () => readJson<R2Status>((r2().status.$get as unknown as Loose)());
export const getR2Summary = () => readJson<R2Summary>((r2().summary.$get as unknown as Loose)());
export const listR2Batches = async () => (await readJson<{ batches: R2Batch[] }>((r2().receipts.batches.$get as unknown as Loose)())).batches;
export const listR2Candidates = async (q?: string) =>
  (await readJson<{ units: R2Candidate[] }>((r2().units.candidates.$get as unknown as Loose)({ query: q ? { q } : {} }))).units;
export const listR2Units = async () => (await readJson<{ units: R2Unit[] }>((r2().units.$get as unknown as Loose)())).units;
export const listR2Draws = async () => (await readJson<{ draws: R2DrawSummary[] }>((r2().samples.$get as unknown as Loose)())).draws;
export const getR2Draw = (id: string) => readJson<R2DrawDetail>((r2().samples[':id'].$get as unknown as Loose)({ param: { id } }));
export const getR2Dpr = () => readJson<R2DprView>((r2().dpr.$get as unknown as Loose)());
export const getR2Competency = async () => (await readJson<{ roster: R2RosterRow[] }>((r2().competency.$get as unknown as Loose)())).roster;

/** Team members, for naming people. Emails live in Cognito; the team endpoint already resolves them for every member. */
export async function listR2TeamMembers(): Promise<R2TeamMember[]> {
  const res = await (api.api.client.me.team.$get as unknown as Loose)();
  await throwIfNotOk(res);
  return (await res.json()) as R2TeamMember[];
}

async function send(p: Promise<Response>): Promise<Record<string, unknown>> {
  const res = await p;
  await throwIfNotOk(res);
  return (await res.json()) as Record<string, unknown>;
}

export const createR2Batch = (json: { receivedOn: string; declaredQty: number; label?: string | null }) =>
  send((r2().receipts.batches.$post as unknown as Loose)({ json }));
export const receiveR2Units = (id: string, assetIds: string[]) =>
  send((r2().receipts.batches[':id'].units.$post as unknown as Loose)({ param: { id }, json: { assetIds } }));
export const closeR2Batch = (id: string, outcome: 'reconciled' | 'discrepancy_supplier_notified' | 'abandoned') =>
  send((r2().receipts.batches[':id'].close.$post as unknown as Loose)({ param: { id }, json: { outcome } }));
export const disposeR2Unit = (assetId: string, json: { kind: string; reasonCode: string; disposedOn: string; evidenceSha256?: string | null }) =>
  send((r2().units[':assetId'].disposition.$post as unknown as Loose)({ param: { assetId }, json }));
export const drawR2Sample = (periodStart: string) => send((r2().samples.$post as unknown as Loose)({ json: { periodStart } }));
export const recordR2Result = (id: string, json: Record<string, unknown>) =>
  send((r2().samples[':id'].results.$post as unknown as Loose)({ param: { id }, json }));
export const recordR2Remediation = (id: string, json: { certificateId: string; note?: string | null }) =>
  send((r2().samples[':id'].remediation.$post as unknown as Loose)({ param: { id }, json }));
export const designateR2Dpr = (designeeId: string, action: 'designate' | 'revoke') =>
  send((r2().dpr.designations.$post as unknown as Loose)({ json: { designeeId, action } }));
export const releaseR2Unit = (json: { assetId: string; basis: string; reasonCode?: string | null }) =>
  send((r2().dpr.releases.$post as unknown as Loose)({ json }));
export const recordR2Competency = (json: Record<string, unknown>) => send((r2().competency.$post as unknown as Loose)({ json }));

/** Plain text for a refusal. Static; nothing the caller typed is echoed. */
export function r2ErrorMessage(err: unknown): string {
  const msg = (err as { body?: { error?: { message?: unknown } } } | null)?.body?.error?.message;
  return typeof msg === 'string' ? msg : 'Could not complete the request. Try again.';
}

export const BASIS_LABEL: Record<string, string> = {
  all_data_devices_sanitized: 'Every data drive has a passed erase record',
  no_data_device: 'The unit has no data drive',
  destroyed: 'Destroyed',
};
export const COMPETENCY_LABEL: Record<string, string> = {
  bench_operation: 'Bench operation',
  data_sanitization: 'Data sanitization and verification',
  dpr: 'Data Protection Representative',
};
